import { findDeepGetActivityOfTask } from '../../../../views/lookahead/planification/index.helper';
import { findDeepGetTask } from '../../GanttVisualization.helper';

export const getClassOdd = (task, column) => {
  let isOdd = false;
  if (
    window.ganttVisualization?.oddColsConfig &&
    task?.type === 'activitytask'
  ) {
    isOdd = window.ganttVisualization.oddColsConfig[column.name];
  }
  return isOdd ? 'odd-col' : 'non-odd-col';
};

export const setUpModificationRequest = ({
  onlyRead,
  task,
  activityReference,
  handleActivityModificationRequest
}) => {
  if (
    !window.ganttVisualization?.lookaheadTemplates_actionActivityModification
  ) {
    window.ganttVisualization.lookaheadTemplates_actionActivityModification = (
      taskId
    ) => {
      if (!onlyRead) {
        const currentTask = findDeepGetTask(window.activities, 'id', taskId);
        if (!currentTask) return;

        const currentActivity =
          taskId === task?.id
            ? activityReference
            : findDeepGetActivityOfTask(window.activities, 'id', taskId);
        if (!currentActivity) return;

        handleActivityModificationRequest(currentTask, currentActivity);
      }
    };
  }
};

/**
 * Formats a date according to the format specified in window.ganttVisualization.currentDateFormat.
 * This implementation replaces moment.js with native JavaScript methods to improve performance,
 * especially in high-frequency operations like Gantt chart scrolling.
 *
 * Supported formats:
 * - MM/DD/YY     -> "12/23/24"
 * - DD/MM/YY     -> "23/12/24"
 * - MM/DD/YYYY   -> "12/23/2024"
 * - DD/MM/YYYY   -> "23/12/2024"
 * - MM/DD/YY HH:mm -> "12/23/24 16:00"
 * - DD/MM/YY HH:mm -> "23/12/24 16:00"
 *
 * @param {Date|string} date - Date to format. Can be a Date object or a string in format
 *                            "Mon Dec 23 2024 16:00:00 GMT+0100 (Central European Standard Time)"
 *
 * @returns {string} Date formatted according to window.ganttVisualization.currentDateFormat
 *                   If no format is specified, defaults to 'MM/DD/YY'
 *
 * @example
 * // If window.ganttVisualization.currentDateFormat is "MM/DD/YY"
 * formatDate("Mon Dec 23 2024 16:00:00 GMT+0100") // returns "12/23/24"
 *
 * @example
 * // If window.ganttVisualization.currentDateFormat is "DD/MM/YY HH:mm"
 * formatDate("Mon Dec 23 2024 16:00:00 GMT+0100") // returns "23/12/24 16:00"
 *
 * @performance This implementation is more efficient than moment.js because:
 * 1. Uses native JavaScript methods (Date)
 * 2. Doesn't load a 300KB external library
 * 3. Performs fewer operations for formatting
 * 4. Better suited for high-frequency operations
 */
export const formatDate = (date) => {
  if (!date) return '';

  const d = new Date(date);
  if (isNaN(d.getTime())) return '';

  const format =
    window.ganttVisualization?.currentDateFormat?.replace('hh', 'HH') ||
    'MM/DD/YY';

  const month = (d.getMonth() + 1).toString().padStart(2, '0');
  const day = d.getDate().toString().padStart(2, '0');
  const year = d.getFullYear();
  const shortYear = year.toString().slice(-2);

  const firstPart = format.startsWith('DD')
    ? `${day}/${month}`
    : `${month}/${day}`;

  const secondPart = format.includes('YYYY') ? year : shortYear;

  const timePart = format.includes('HH:mm')
    ? ` ${d.getHours().toString().padStart(2, '0')}:${d
        .getMinutes()
        .toString()
        .padStart(2, '0')}`
    : '';

  return `${firstPart}/${secondPart}${timePart}`;
};
